import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownLeft, ArrowUpLeft, ChevronDown, Eye, FileText } from 'lucide-react';
import { KIND_LABELS, ORIGIN_LABELS, PAYMENT_LABELS, STATUS_LABELS, dateLabel, linkage, money } from '../accounting/workshopFinanceModel';
import './OperationsCards.css';

// Raw document amounts remain decimal strings from the canonical reader.
// This card neither derives balances nor exposes financial write actions.
const DocumentMoney = ({ value }) => {
    const formatted = money(value);
    return <span className={`operations-money${formatted === 'غير متاح' ? ' operations-money-unavailable' : ''}`}>
        <bdi dir="ltr">{formatted}</bdi>
    </span>;
};

const Field = ({ label, children }) => <div><dt>{label}</dt><dd>{children}</dd></div>;

export default function OperationDocumentCard({ document, canOpenVehicle = false }) {
    const [expanded, setExpanded] = useState(false);
    const income = document.kind === 'income';
    const linked = linkage(document);
    const Icon = income ? ArrowDownLeft : ArrowUpLeft;
    const contentId = `operations-document-details-${document.id}`;
    const postingStatus = ['posted', 'reversed'].includes(document.status) ? STATUS_LABELS[document.status] : 'غير مسجلة';
    const settlementStatus = ['paid', 'partial', 'unpaid', 'reversed'].includes(document.settlement_status) ? STATUS_LABELS[document.settlement_status] : 'غير متاحة';
    const canLinkVehicle = canOpenVehicle && document.link_type === 'vehicle' && document.vehicle?.id;
    return <article
        className={`operations-agreed-card operations-document-card operations-tone-${income ? 'collection' : 'workshop'}`}
        data-testid={`operations-document-card-${document.id}`} data-card="document" data-document-kind={document.kind}
    >
        <div className="operations-card-top">
            <div className="operations-card-identity">
                <span className="operations-card-icon" aria-hidden="true"><Icon size={27} strokeWidth={1.9} /></span>
                <div className="operations-card-identity-copy">
                    <p className="operations-card-kind">{document.kind === 'payroll' ? 'راتب' : KIND_LABELS[document.kind] || 'حركة مستقلة'}</p>
                    <h2>{document.description || 'الوصف غير مسجل'}</h2>
                    <p className="operations-card-reference">مستند <bdi>{document.number || document.id}</bdi></p>
                </div>
            </div>
            <div className="operations-card-main-total" data-testid={`operations-document-amount-${document.id}`}>
                <span>المبلغ</span><DocumentMoney value={document.amount} />
            </div>
        </div>
        <div className="operations-card-badges">
            <span className="operations-badge" data-testid={`operations-document-status-${document.id}`}>القيد: {postingStatus}</span>
            <span className="operations-badge" data-testid={`operations-document-settlement-${document.id}`}>{income ? 'التحصيل' : 'السداد'}: {settlementStatus}</span>
        </div>
        <div className="operations-event-subject">
            <strong>{linked.label}{linked.detail ? `: ${linked.detail}` : ''}</strong>
        </div>
        <p className="operations-small-note">حركة مستقلة عن بنود الزيارة واعتمادها.</p>
        {expanded && <div className="operations-card-expanded" id={contentId} data-testid={`operations-document-details-${document.id}`}>
            <dl className="operations-metrics">
                <div className="operations-metric" data-testid={`operations-document-paid-${document.id}`}>
                    <dt>{income ? 'المحصّل' : 'المسدّد'}</dt><dd><DocumentMoney value={document.paid_amount} /></dd>
                </div>
                <div className="operations-metric" data-testid={`operations-document-outstanding-${document.id}`}>
                    <dt>{income ? 'المتبقي لصالح الورشة' : 'المتبقي للجهة'}</dt><dd><DocumentMoney value={document.outstanding_amount} /></dd>
                </div>
            </dl>
            <dl className="operations-provenance">
                <Field label="الحساب">{document.account?.name || 'غير متاح'}{document.account?.code && <> · <bdi>{document.account.code}</bdi></>}</Field>
                <Field label="مصدر الإدخال">{ORIGIN_LABELS[document.origin] || 'غير مسجل'}</Field>
                <Field label="سجّلها">{document.actor_name || 'غير مسجل'}</Field>
                <Field label="الوسيلة">{PAYMENT_LABELS[document.payment_method] || 'غير مسجلة'}</Field>
                {document.supplier?.id && <Field label="المورد">{document.supplier.name || 'غير مسجل'}</Field>}
                {document.payee_name && <Field label={document.kind === 'payroll' ? 'الموظف' : 'الجهة'}>{document.payee_name}</Field>}
                {document.payroll_period && <Field label="شهر الراتب"><bdi>{document.payroll_period}</bdi></Field>}
            </dl>
        </div>}
        <div className="operations-card-footer">
            <time dateTime={document.entry_date || undefined}>{dateLabel(document.entry_date)}</time>
            <Link className="operations-vehicle-link" to={`/accounting/journal-entries?document=${encodeURIComponent(document.id)}`} data-testid={`operations-document-journal-${document.id}`}>
                <FileText size={17} aria-hidden="true" />عرض المستند
            </Link>
            {expanded && canLinkVehicle && <Link className="operations-vehicle-link" to={`/vehicle/${encodeURIComponent(document.vehicle.id)}`} data-testid={`operations-document-vehicle-${document.id}`}>
                <Eye size={17} aria-hidden="true" />فتح ملف المركبة
            </Link>}
        </div>
        <button type="button" className="operations-card-expand" onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls={contentId} data-testid={`operations-document-toggle-${document.id}`}>
            <ChevronDown size={22} className={expanded ? 'is-expanded' : ''} aria-hidden="true" />{expanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
        </button>
    </article>;
}
